// The app's single toast surface: a solid, filled pill floating at
// bottom-centre, with a ringed status icon, the message, and an inline
// dismiss.
//
// Custom renderer (via the <Toaster> render prop) instead of
// `toastOptions.style` so every `toast.success`/`error`/`loading` call
// site across the app gets restyled without touching the call sites
// themselves. Trade-off: this replaces react-hot-toast's own ToastBar
// chrome, so a caller-supplied `icon` has to be read manually (see below).
//
// Fills encode toast STATUS (real state), so a green success / red error
// fill isn't decoration here. Fills are the 700 stops, not 500/600: the
// message is 14px white text on the fill, needing 4.5:1 (WCAG AA, normal
// text). success-700 = 5.02:1, danger-700 = 6.47:1, gray-900 = 17.7:1;
// white-on-success-500 is 2.28:1 and fails.
import { useState, useLayoutEffect, useRef } from 'react'
import { Toaster, ToastBar, toast } from 'react-hot-toast'
import { Check, X, AlertTriangle, Loader2 } from 'lucide-react'

// Literal class strings per variant — never build these by interpolation,
// Tailwind's JIT purges anything it can't see as a literal.
// Exported (only) so the lookup/fallback below is unit-testable — no
// behaviour change.
export const VARIANT = {
  success: {
    surface: 'bg-success-700',
    ring: 'bg-white/15 ring-1 ring-white/25',
    Icon: Check,
  },
  error: {
    surface: 'bg-danger-700',
    ring: 'bg-white/15 ring-1 ring-white/25',
    Icon: AlertTriangle,
  },
  loading: {
    surface: 'bg-gray-900',
    ring: 'bg-white/10 ring-1 ring-white/20',
    Icon: Loader2,
  },
  blank: {
    surface: 'bg-gray-900',
    ring: 'bg-white/10 ring-1 ring-white/20',
    Icon: Check,
  },
}

// react-hot-toast toast types we don't have a dedicated fill for (e.g. a
// custom `t.type` some future call site introduces) must degrade to the
// neutral `blank` variant rather than rendering `undefined` surface/ring
// classes.
export function resolveToastVariant(type) {
  return VARIANT[type] ?? VARIANT.blank
}

// The pill. Split out as its own component purely so it can hold a hook.
//
// Radius depends on whether the message WRAPS: `rounded-full` reads as a pill
// for a one-liner, but a fully-rounded multi-line block is a "stadium blob".
// Measures the rendered box rather than `message.length` — wrapping depends
// on viewport width, not character count, and `t.message` can be a function
// (react-hot-toast's `toast(t => …)` form, used by DataImportPage), so a
// string-length check wouldn't even apply to it.
function ToastPill({ t, surface, ring, icon }) {
  const messageRef = useRef(null)
  const [isWrapped, setIsWrapped] = useState(false)

  useLayoutEffect(() => {
    const el = messageRef.current
    if (!el) return

    const measure = () => {
      // More than ~1.5 line-boxes tall means it wrapped. Compare against the
      // element's own computed line-height rather than a hard-coded px value,
      // so this survives a change to the type scale.
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) return
      setIsWrapped(el.scrollHeight > lineHeight * 1.5)
    }

    measure()

    // Re-measure on resize: a message that fits on one line at desktop width
    // can wrap once the viewport narrows, and the radius has to follow.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [t.message])

  const radius = isWrapped ? 'rounded-2xl' : 'rounded-full'
  const align = isWrapped ? 'items-start' : 'items-center'

  return (
    <div
      className={`flex ${align} gap-3 ${radius} ${surface} py-2.5 pl-3 pr-2.5 shadow-elev-3 max-w-[min(90vw,32rem)]`}
      role={t.type === 'error' ? 'alert' : 'status'}
    >
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${ring}`}>
        {icon}
      </span>

      <p
        ref={messageRef}
        className={`text-sm font-medium leading-snug text-white ${isWrapped ? 'py-1' : ''}`}
      >
        {typeof t.message === 'function' ? t.message(t) : t.message}
      </p>

      {/* A loading toast is dismissed programmatically when its promise settles —
          offering an X there would let the user close a toast that is still doing
          work, so it only renders on settled types. */}
      {t.type !== 'loading' && (
        <button
          type="button"
          onClick={() => toast.dismiss(t.id)}
          aria-label="Dismiss notification"
          className="ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/70 transition hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <X className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

export default function AppToaster() {
  return (
    <Toaster
      position="bottom-center"
      gutter={10}
      containerClassName="!bottom-6"
      toastOptions={{
        duration: 4000,
        success: { duration: 3000 },
        // Errors get longer — they usually carry something the user has to read.
        error: { duration: 5000 },
      }}
    >
      {(t) => {
        const { surface, ring, Icon } = resolveToastVariant(t.type)

        // Honour a caller-supplied `icon` (e.g. toast(msg, { icon: <AlertTriangle/> }))
        // — react-hot-toast's default ToastBar reads `t.icon` and this renderer
        // replaces that chrome, so it has to read it too. Variant icon is the fallback.
        const icon = t.icon ?? (
          <Icon
            className={t.type === 'loading' ? 'h-4 w-4 text-white animate-spin' : 'h-4 w-4 text-white'}
            strokeWidth={1.75}
            aria-hidden="true"
          />
        )

        return (
          // ToastBar supplies react-hot-toast's own enter/exit animation and the
          // `t.visible` lifecycle; we throw away its default chrome and render
          // our own pill inside it.
          <ToastBar toast={t} style={{ background: 'transparent', boxShadow: 'none', padding: 0, maxWidth: 'none' }}>
            {() => <ToastPill t={t} surface={surface} ring={ring} icon={icon} />}
          </ToastBar>
        )
      }}
    </Toaster>
  )
}
